import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import ProtectedRoute from '../Components/Auth/ProtectedRoute';
import AdminRoute from '../Components/Auth/AdminRoute';
import ScrollToTop from '../Components/Navigation/ScrollToTop';
import TopProgressBar from '../Components/Loading/TopProgressBar';
import BarLoading from '../Components/Loading/BarLoading';
import CookieConsent from '../Components/CookieConsent/CookieConsent';

const Home = lazy(() => import('../Pages/Home'));
const Signup = lazy(() => import('../Pages/Signup'));
const Login = lazy(() => import('../Pages/Login'));
const ForgotPasswordPage = lazy(() => import('../Pages/ForgotPassword'));
const CreatePost = lazy(() => import('../Pages/CreatePost'));
const ViewPost = lazy(() => import('../Pages/ViewPost'));
const ViewMore = lazy(() => import('../Pages/ViewMore'));
const CategoryPageRoute = lazy(() => import('../Pages/CategoryPage'));
const NotFound = lazy(() => import('../Pages/NotFound'));
const SearchResultsPage = lazy(() => import('../Pages/SearchResults'));
const Dashboard = lazy(() => import('../Pages/Dashboard'));
const Profile = lazy(() => import('../Pages/Profile'));
const EditProfile = lazy(() => import('../Pages/EditProfile'));
const EditAdPage = lazy(() => import('../Pages/EditAd'));
const MessagesPage = lazy(() => import('../Pages/Messages'));
const ChatPage = lazy(() => import('../Pages/Chat'));
const NotificationsPage = lazy(() => import('../Pages/Notifications'));
const AboutPage = lazy(() => import('../Pages/AboutPage'));
const CareersPage = lazy(() => import('../Pages/CareersPage'));
const ContactPage = lazy(() => import('../Pages/ContactPage'));
const PeoplePage = lazy(() => import('../Pages/PeoplePage'));
const HelpPage = lazy(() => import('../Pages/HelpPage'));
const SitemapPage = lazy(() => import('../Pages/SitemapPage'));
const LegalPage = lazy(() => import('../Pages/LegalPage'));
const MarketplaceReversoPage = lazy(
  () => import('../Pages/MarketplaceReversoPage')
);
const Nearby = lazy(() => import('../Pages/Nearby'));
const ProfileIndex = lazy(() => import('../Components/Auth/ProfileIndex'));
const SellerStore = lazy(() => import('../Pages/SellerStore'));
const PromoteAd = lazy(() => import('../Pages/PromoteAd'));
const Compare = lazy(() => import('../Pages/Compare'));
const TransactionsPage = lazy(() => import('../Pages/Transactions'));
const AddressesPage = lazy(() => import('../Pages/Addresses'));
const FollowersPage = lazy(() => import('../Pages/FollowersPage'));

const AdminDashboard = lazy(() => import('../Pages/Admin/AdminDashboard'));
const AdminUsers = lazy(() => import('../Pages/Admin/AdminUsers'));
const AdminAds = lazy(() => import('../Pages/Admin/AdminAds'));
const AdminReports = lazy(() => import('../Pages/Admin/AdminReports'));
const AdminCategories = lazy(() => import('../Pages/Admin/AdminCategories'));
const AdminAnalytics = lazy(() => import('../Pages/Admin/AdminAnalytics'));
const AdminVerifications = lazy(
  () => import('../Pages/Admin/AdminVerifications')
);
const AdminBanners = lazy(() => import('../Pages/Admin/AdminBanners'));
const AdminTrending = lazy(() => import('../Pages/Admin/AdminTrending'));
const AdminReviews = lazy(() => import('../Pages/Admin/AdminReviews'));
const AdminChat = lazy(() => import('../Pages/Admin/AdminChat'));
const AdminTransactions = lazy(
  () => import('../Pages/Admin/AdminTransactions')
);
const AdminFeedback = lazy(() => import('../Pages/Admin/AdminFeedback'));
const AdminActivityLog = lazy(() => import('../Pages/Admin/AdminActivityLog'));
const AdminSettings = lazy(() => import('../Pages/Admin/AdminSettings'));
const AdminAdTypes = lazy(() => import('../Pages/Admin/AdminAdTypes'));
const AdminWorkers = lazy(() => import('../Pages/Admin/AdminWorkers'));
const AdminEmails = lazy(() => import('../Pages/Admin/AdminEmails'));
const AdminMarketing = lazy(() => import('../Pages/Admin/AdminMarketing'));
const AdminSmsCampaigns = lazy(
  () => import('../Pages/Admin/AdminSmsCampaigns')
);
const AdminEmailCampaigns = lazy(
  () => import('../Pages/Admin/AdminEmailCampaigns')
);
const FeaturedRequestsModeration = lazy(
  () => import('../Components/Admin/FeaturedRequestsModeration')
);

function RouteLoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <BarLoading />
    </div>
  );
}

function MainRoutes() {
  return (
    <Router>
      <TopProgressBar />
      <ScrollToTop />
      <CookieConsent />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>
          <Route path="/signup">
            <Signup />
          </Route>
          <Route exact path="/login">
            <Login />
          </Route>
          <Route path="/forgot-password">
            <ForgotPasswordPage />
          </Route>
          <Route exact path="/search/location/:citySlug/:typeSlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search/location/:citySlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search/categoria/:categorySlug/:subcategorySlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search/categoria/:categorySlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search/tipo/:typeSlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search/tag/:tagSlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search/busca/:searchSlug">
            <SearchResultsPage />
          </Route>
          <Route exact path="/search">
            <SearchResultsPage />
          </Route>
          <Route exact path="/category/:categoryId">
            <CategoryPageRoute />
          </Route>
          <ProtectedRoute exact path="/ad/:slug/:adId/edit">
            <EditAdPage />
          </ProtectedRoute>
          <ProtectedRoute exact path="/ad/:adId/edit">
            <EditAdPage />
          </ProtectedRoute>
          <ProtectedRoute exact path="/ad/:slug/:adId/promote">
            <PromoteAd />
          </ProtectedRoute>
          <ProtectedRoute exact path="/ad/:adId/promote">
            <PromoteAd />
          </ProtectedRoute>
          <Route exact path="/view">
            <ViewPost />
          </Route>
          <Route exact path="/ad/:slug/:adId">
            <ViewPost />
          </Route>
          <Route exact path="/ad/:adId">
            <ViewPost />
          </Route>
          <Route exact path="/viewmore">
            <ViewMore />
          </Route>
          <Route exact path="/profile">
            <ProfileIndex />
          </Route>
          <ProtectedRoute exact path="/profile/edit">
            <EditProfile />
          </ProtectedRoute>
          <Route exact path="/profile/:userId/followers">
            <FollowersPage />
          </Route>
          <Route exact path="/profile/:userId/following">
            <FollowersPage />
          </Route>
          <Route exact path="/profile/:userId">
            <Profile />
          </Route>
          <Route exact path="/seller/:userId/store">
            <SellerStore />
          </Route>
          <ProtectedRoute exact path="/create">
            <CreatePost />
          </ProtectedRoute>
          <ProtectedRoute path="/dashboard/transactions">
            <TransactionsPage />
          </ProtectedRoute>
          <ProtectedRoute path="/dashboard/addresses">
            <AddressesPage />
          </ProtectedRoute>
          <ProtectedRoute path="/dashboard">
            <Dashboard />
          </ProtectedRoute>
          <ProtectedRoute path="/notifications">
            <NotificationsPage />
          </ProtectedRoute>
          <ProtectedRoute path="/messages">
            <MessagesPage />
          </ProtectedRoute>
          <ProtectedRoute path="/chat/:conversationId">
            <ChatPage />
          </ProtectedRoute>
          <Route path="/about">
            <AboutPage />
          </Route>
          <Route path="/careers">
            <CareersPage />
          </Route>
          <Route path="/contact">
            <ContactPage />
          </Route>
          <Route path="/people">
            <PeoplePage />
          </Route>
          <Route path="/help">
            <HelpPage />
          </Route>
          <Route path="/sitemap">
            <SitemapPage />
          </Route>
          <Route path="/legal">
            <LegalPage />
          </Route>
          <Route path="/marketplace-reverso">
            <MarketplaceReversoPage />
          </Route>
          <Route path="/nearby">
            <Nearby />
          </Route>
          <Route path="/compare">
            <Compare />
          </Route>
          <AdminRoute exact path="/admin">
            <AdminDashboard />
          </AdminRoute>
          <AdminRoute exact path="/admin/users">
            <AdminUsers />
          </AdminRoute>
          <AdminRoute path="/admin/users/:userId">
            <AdminUsers />
          </AdminRoute>
          <AdminRoute path="/admin/ads">
            <AdminAds />
          </AdminRoute>
          <AdminRoute path="/admin/verifications">
            <AdminVerifications />
          </AdminRoute>
          <AdminRoute path="/admin/reports">
            <AdminReports />
          </AdminRoute>
          <AdminRoute path="/admin/categories">
            <AdminCategories />
          </AdminRoute>
          <AdminRoute path="/admin/analytics">
            <AdminAnalytics />
          </AdminRoute>
          <AdminRoute path="/admin/banners">
            <AdminBanners />
          </AdminRoute>
          <AdminRoute path="/admin/trending">
            <AdminTrending />
          </AdminRoute>
          <AdminRoute path="/admin/reviews">
            <AdminReviews />
          </AdminRoute>
          <AdminRoute path="/admin/featured-requests">
            <FeaturedRequestsModeration />
          </AdminRoute>
          <AdminRoute path="/admin/chat">
            <AdminChat />
          </AdminRoute>
          <AdminRoute path="/admin/transactions">
            <AdminTransactions />
          </AdminRoute>
          <AdminRoute path="/admin/feedback">
            <AdminFeedback />
          </AdminRoute>
          <AdminRoute path="/admin/activity">
            <AdminActivityLog />
          </AdminRoute>
          <AdminRoute path="/admin/settings">
            <AdminSettings />
          </AdminRoute>
          <AdminRoute path="/admin/emails">
            <AdminEmails />
          </AdminRoute>
          <AdminRoute path="/admin/sms">
            <AdminSmsCampaigns />
          </AdminRoute>
          <AdminRoute path="/admin/email-campaigns">
            <AdminEmailCampaigns />
          </AdminRoute>
          <AdminRoute path="/admin/marketing">
            <AdminMarketing />
          </AdminRoute>
          <AdminRoute path="/admin/ad-types">
            <AdminAdTypes />
          </AdminRoute>
          <AdminRoute path="/admin/workers">
            <AdminWorkers />
          </AdminRoute>
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Suspense>
    </Router>
  );
}

export default MainRoutes;
